import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";
import type { BuildingDataAttributeInsert } from "@/stores/admin/models";
import { ATTRIBUTE_TYPES, VALUE_TYPES, MEASUREMENT_UNITS } from "../../constants/attributeTypes";

const GROUP_OPTIONS = [
  { id: 2, name: "Space" },
  { id: 3, name: "System" },
  { id: 4, name: "Component" },
  { id: 6, name: "Data" },
];

interface CreateAttributeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAttributeDialog({ open, onOpenChange }: CreateAttributeDialogProps) {
  const { buildingDataAttributesStore } = useStores();
  const [attributeName, setAttributeName] = useState("");
  const [groupId, setGroupId] = useState<string>("");
  const [nameEn, setNameEn] = useState("");
  const [nameEt, setNameEt] = useState("");
  const [attributeType, setAttributeType] = useState("");
  const [valueType, setValueType] = useState("");
  const [unit, setUnit] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const resetForm = () => {
    setAttributeName("");
    setGroupId("");
    setNameEn("");
    setNameEt("");
    setAttributeType("");
    setValueType("");
    setUnit("");
  };

  const handleCreate = async () => {
    if (!attributeName.trim()) {
      toast.error("Attribute name is required");
      return;
    }

    if (!groupId) {
      toast.error("Group is required");
      return;
    }

    const selectedGroup = GROUP_OPTIONS.find((g) => g.id.toString() === groupId);

    if (!selectedGroup) {
      toast.error("Selected group not found");
      return;
    }

    setIsCreating(true);
    try {
      const newAttribute: BuildingDataAttributeInsert = {
        attribute_nm: attributeName.trim(),
        group_id: parseInt(groupId),
        group_nm: selectedGroup.name,
        name_en: nameEn.trim() || null,
        name_et: nameEt.trim() || null,
        attribute_type_en: attributeType.trim() || null,
        value_type: valueType.trim() || null,
        default_unit: unit.trim() || null,
      };

      await buildingDataAttributesStore.createAttribute(newAttribute);
      toast.success("Attribute created successfully");
      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to create attribute");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isCreating) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Attribute</DialogTitle>
          <DialogDescription>
            Add a new building data attribute with its properties and metadata.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="attribute_nm">Attribute Name *</Label>
            <Input
              id="attribute_nm"
              value={attributeName}
              onChange={(e) => setAttributeName(e.target.value)}
              placeholder="e.g., building_height"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group_id">Group *</Label>
            <Select value={groupId} onValueChange={setGroupId} disabled={isCreating}>
              <SelectTrigger id="group_id">
                <SelectValue placeholder="Select a group..." />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((group) => (
                  <SelectItem key={group.id} value={group.id.toString()}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="name_en">Name (English)</Label>
            <Input
              id="name_en"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              placeholder="e.g., Building Height"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="name_et">Name (Estonian)</Label>
            <Input
              id="name_et"
              value={nameEt}
              onChange={(e) => setNameEt(e.target.value)}
              placeholder="e.g., Hoone kõrgus"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="attribute_type_en">Attribute Type</Label>
            <Select value={attributeType} onValueChange={setAttributeType} disabled={isCreating}>
              <SelectTrigger id="attribute_type_en">
                <SelectValue placeholder="Select attribute type..." />
              </SelectTrigger>
              <SelectContent>
                {ATTRIBUTE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="value_type">Value Type</Label>
            <Select value={valueType} onValueChange={setValueType} disabled={isCreating}>
              <SelectTrigger id="value_type">
                <SelectValue placeholder="Select value type..." />
              </SelectTrigger>
              <SelectContent>
                {VALUE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="default_unit">Unit</Label>
            <Select value={unit} onValueChange={setUnit} disabled={isCreating}>
              <SelectTrigger id="default_unit">
                <SelectValue placeholder="Select unit..." />
              </SelectTrigger>
              <SelectContent>
                {MEASUREMENT_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Attribute"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
